import { Button, makeStyles } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { fadeInRight, fadeInLeft, fadeIn, fadeInUp } from 'react-animations';
import { StyleRoot, keyframes } from 'radium';
import "./About.css";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { useTranslation } from "react-i18next";
import BasicModal from "../../components/Modal/BasicModal"
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';

const About = ({ fetchQuestions, openModal, handleOpenModal, handleCloseModal, removeLocalStorage, lng }) => {
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0)
    ReactGA.send({ hitType: "pageview", page: window.location.pathname});
  }, []);

  const handleSubmit = async () => {
    handleCloseModal();
    const data = await fetchQuestions();

    if (Array.isArray(data)) {
      removeLocalStorage();

      let testRoute = lng !== "en" ? `/${lng}/testing/` : `/testing/`;
      history.push(testRoute);
    } else {
      window.scrollTo(0, 0)
      setError(true);
    }
  };

  const useStyles = makeStyles({
    button2: {
      backgroundColor: "#F49F1C",
      borderRadius: "30px",
      fontSize: "20px",
      padding: "20px 30px",
      fontWeight: "bold",
      boxShadow: "0 4px 14px 0 rgba(244, 159, 28, 0.39)",
      '&:hover': {
        backgroundColor: '#030E4F',
        "& .MuiButton-label": {
          color: '#F49F1C'
        }
      }
    }
  })

  const classes = useStyles()

  const styles = {
    fadeInUp: {
      animation: 'x 0.5s',
      animationName: keyframes(fadeInUp, 'fadeInUp'),
      animationDelay: '1.3s',
      animationFillMode: 'forwards',
      opacity: 0
    },
    fadeInRight: {
      animation: 'x 0.8s',
      animationName: keyframes(fadeInRight, 'fadeInRight')
    },
    fadeInLeft1: {
      animation: 'x 0.8s',
      animationName: keyframes(fadeInLeft, 'fadeInLeft')
    },
    fadeInLeft2: {
      animation: 'x 0.8s',
      animationName: keyframes(fadeInLeft, 'fadeInLeft'),
      animationDelay: '0.2s',
      animationFillMode: 'forwards',
      opacity: 0
    },
    fadeIn: {
      animation: 'x 1s',
      animationName: keyframes(fadeIn, 'fadeIn'),
      animationDelay: '0.6s',
      animationFillMode: 'forwards',
      opacity: 0
    }
  }

  const ogLocales = {
    en: 'en_US',
    es: 'es_ES',
    de: 'de_DE',
    fr: 'fr_FR',
    pt: 'pt_BR',
    sr: 'sr_RS'
  };
  
  return (
    <div className="about-page-wrapper">
      <Helmet>
        {/* --- TITLE & CANONICAL --- */}
        <title>{t("Programming Aptitude Quiz & Career Assessment")}</title>
        
        {/* Dodata kosa crta na kraj URL-a zbog Nginx rutinga */}
        <link rel="canonical" href={lng !== "en" ? `https://iscodingforyou.com/${lng}/coding-test/` : `https://iscodingforyou.com/coding-test/`} />
        
        {/* --- DESCRIPTION & KEYWORDS --- */}
        <meta name="description" content={t("Take our free programming aptitude quiz. Evaluate your logical skills, personality fit, and discover which IT career path matches your strengths.")}/>
        {/* <meta name="keywords" content={t("programming aptitude test, coding assessment, it career quiz, developer skills test, logic quiz for coding")} /> */}
        <meta charSet="utf-8" />

        {/* --- HREFLANG TAGOVI (Izuzetno bitno za multijezičnost) --- */}
        <link rel="alternate" hreflang="en" href="https://iscodingforyou.com/coding-test/" />
        <link rel="alternate" hreflang="es" href="https://iscodingforyou.com/es/coding-test/" />
        <link rel="alternate" hreflang="de" href="https://iscodingforyou.com/de/coding-test/" />
        <link rel="alternate" hreflang="fr" href="https://iscodingforyou.com/fr/coding-test/" />
        <link rel="alternate" hreflang="pt-BR" href="https://iscodingforyou.com/pt/coding-test/" />
        <link rel="alternate" hreflang="sr" href="https://iscodingforyou.com/sr/coding-test/" />
        <link rel="alternate" hreflang="x-default" href="https://iscodingforyou.com/coding-test/" />
        
        {/* --- OPEN GRAPH (Facebook/LinkedIn) --- */}
        <meta property="og:title" key="og:title" content={t("Programming Aptitude Quiz & Career Assessment")} />
        <meta property="og:site_name" key="og:site_name" content="IsCodingForYou.com" />
        <meta property="og:url" key="og:url" content={lng !== "en" ? `https://iscodingforyou.com/${lng}/coding-test/` : `https://iscodingforyou.com/coding-test/`} />
        <meta property="og:type" key="og:type" content="website" />
        <meta property="og:description" key="og:description" content={t("Take our free programming aptitude quiz. Evaluate your logical skills, personality fit, and discover which IT career path matches your strengths.")} />
        <meta property="og:image" key="og:image" content="https://iscodingforyou.com/about-image2.png" />
        
        {/* Dinamički locale */}
        <meta property="og:locale" key="og:locale" content={ogLocales[lng] || 'en_US'} />
        <meta property="og:locale:alternate" content="sr_RS" />
        <meta property="og:locale:alternate" content="de_DE" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:locale:alternate" content="es_ES" />
        <meta property="og:locale:alternate" content="fr_FR" />
        <meta property="og:locale:alternate" content="pt_BR" />

        {/* --- TWITTER CARD --- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="https://iscodingforyou.com/" />
        <meta name="twitter:title" content={t("Programming Aptitude Quiz & Career Assessment")} />
        <meta name="twitter:image" content="https://iscodingforyou.com/about-image2.png"/>
        <meta name="twitter:description" content={t("Take our free programming aptitude quiz. Evaluate your logical skills, personality fit, and discover which IT career path matches your strengths.")} />
      </Helmet>

      <BasicModal
        handleSubmit={handleSubmit}
        openModal={openModal}
        handleCloseModal={handleCloseModal}
      />

      {/* HERO SEKCIJA */}
      <div className="content-about">
        <div className="settings-about">
          <div className="settings__select">
            {error && <ErrorMessage>{t("Something went wrong. Our team is working to solve it.")}</ErrorMessage>}
            <StyleRoot>
              <h1 className="about-hero-title" style={styles.fadeInLeft1}>
                 {t("Programming Aptitude")} <span className="title-coding-about">{t("Assessment")}</span>
              </h1>
              <h2 className="about-hero-subtitle" style={styles.fadeInLeft2}>
                {t("Discover if you have the natural logic and mindset to become a software engineer.")}
              </h2>
              
              <div className="about-intro-text" style={styles.fadeIn}>
                <p>
                  {t("This comprehensive assessment is developed by industry experts to evaluate your problem-solving skills and personality traits. It goes beyond basic coding syntax to analyze your core potential.")}
                </p>
                <p>
                  {t("No prior coding knowledge is required. You will need about 15 minutes of uninterrupted focus.")}
                </p>
              </div>

              <div style={styles.fadeInUp} className="about-cta-wrapper">
                <Button
                  className={classes.button2}
                  variant="contained"
                  size="large"
                  onClick={handleOpenModal}
                >
                  {t("Start Assessment")} {String.fromCodePoint(0x2192)}
                </Button>
              </div>
            </StyleRoot>
          </div>
        </div>
        <div className="banner-about">
          <StyleRoot>
            <img src="/about-image2.png" className="banner-image app-image" style={styles.fadeInRight} alt={t("Programming Aptitude Quiz")} width="530" height="502.72"/>
          </StyleRoot>
        </div>
      </div>

      {/* NOVA SEKCIJA: Kako test radi (Za "Thick Content") */}
      <div className="about-methodology-section">
        <div className="methodology-container">
            <h2 className="methodology-title">{t("How the Assessment Works")}</h2>
            <p className="methodology-subtitle">{t("We analyze 3 core pillars essential for a successful tech career.")}</p>
            
            <div className="methodology-grid">
                <div className="methodology-card">
                    <div className="method-icon">🧠</div>
                    <h3>{t("1. Logical Reasoning")}</h3>
                    <p>{t("Evaluate your ability to recognize patterns, solve mathematical puzzles, and process complex information quickly.")}</p>
                </div>
                <div className="methodology-card">
                    <div className="method-icon">🎭</div>
                    <h3>{t("2. Personality Match")}</h3>
                    <p>{t("Software development is a team sport. We assess if you thrive in collaborative agile teams or prefer independent tasks.")}</p>
                </div>
                <div className="methodology-card">
                    <div className="methodology-icon">🛣️</div>
                    <h3>{t("3. Career Path Mapping")}</h3>
                    <p>{t("Based on your score, we determine if you belong in Frontend, Backend, Full-Stack, or QA Testing.")}</p>
                </div>
            </div>
        </div>
      </div>

      {/* SEKCIJA 2: Ciljevi (Prerađeno za bolji UI) */}
      <div className="home-section-2">
        <div className="content-2 about-goals-content">
          <div className="settings-about-2">
            <h2 className="about-goals">{t("What you will discover")}</h2>
            <div className="about-goals-grid">
              <div className="goal-item">
                <span className="goal-check">✓</span>
                <p><strong>{t("Your Coding Potential:")}</strong> {t("A clear 'Yes' or 'No' on whether a career in software development suits your cognitive strengths.")}</p>
              </div>
              <div className="goal-item">
                <span className="goal-check">✓</span>
                <p><strong>{t("Ideal IT Profession:")}</strong> {t("If coding is for you, we will pinpoint your exact niche (e.g., Frontend vs Backend).")}</p>
              </div>
              <div className="goal-item">
                <span className="goal-check">✓</span>
                <p><strong>{t("Alternative Routes:")}</strong> {t("If programming isn't your strong suit, we provide alternative high-paying tech roles.")}</p>
              </div>
              <div className="goal-item">
                <span className="goal-check">✓</span>
                <p><strong>{t("Actionable Learning Plan:")}</strong> {t("Receive a personalized roadmap on what to learn first based on your results.")}</p>
              </div>
            </div>

            <div className="about-final-cta">
                <p className="home-ready-2">{t("Ready to find your path?")}</p>
                <Button
                className={classes.button2}
                variant="contained"
                size="large"
                onClick={handleOpenModal}
                >
                {t("Take the Quiz")} {String.fromCodePoint(0x2192)}
                </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;